import { BellRingingIcon, CheckCircleIcon, XIcon } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { toast, useToast } from "@/client/hooks/use-toast";
import { useUser } from "@/client/services/user";

import { usePushNotifications } from "@/client/services/notification";
import {
  getPushNudgeBlockReason,
  isPushNudgeOptedIn,
  recordPushNudgeClosed,
  recordPushNudgeDenied,
  recordPushNudgeLater,
  recordPushNudgeShown,
  recordPushOptedIn,
} from "@/client/services/notification/push-nudge";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PUSH OPT-IN NUDGE — toast personnalisée "custom" avec CTA vert (1 clic)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Montée une seule fois (providers global, à côté de `InstallPwaBanner`).
 *
 *  Objectif produit : transformer l'activation des notifications push (web,
 *  VAPID) qui n'existe aujourd'hui que dans Paramètres → Notifications en une
 *  invitation proactive, bienveillante et à fort CTA, dans l'espace client.
 *
 *  Design — carte 100 % custom via `toast.custom` + `unstyled` :
 *   - Aucun style par défaut sonner (évite le CTA noir du wrapper action)
 *   - Carte glassmorphism, responsive, icône dédiée en pastille verte
 *   - CTA principal vert (emerald-500) + action secondaire "Plus tard"
 *   - Bouton de fermeture discret (X)
 *
 *  Cadence (localStorage par appareil) — voir push-nudge.ts :
 *   - 1re apparition ~8 s, "Plus tard" → +48 h, X → +7 j
 *   - Max 3 impressions → pause 14 j ; refus navigateur ×2 → pause 30 j
 *   - Abonné → plus jamais affiché
 * ─────────────────────────────────────────────────────────────────────────────
 */

const NUDGE_DELAY_MS = 8_000;
const NUDGE_DURATION_MS = 15_000;

type NudgeCardProps = {
  toastId: string | number;
  onEnable: () => Promise<void>;
  onLater: () => void;
  onClose: () => void;
};

/** Carte de la toast : design maîtrisé, responsive, CTA vert. */
const NudgeCard = ({ toastId, onEnable, onLater, onClose }: NudgeCardProps) => {
  const [busy, setBusy] = useState(false);

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    await onEnable();
    setBusy(false);
    toast.dismiss(toastId);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="relative flex w-[min(92vw,380px)] items-start gap-3 overflow-hidden rounded-2xl border border-emerald-100 bg-white/95 p-4 shadow-[0_16px_48px_-12px_rgba(16,185,129,0.35)] backdrop-blur-xl sm:shadow-[0_24px_64px_-12px_rgba(16,185,129,0.4)]"
    >
      {/* Halo décoratif en haut à gauche */}
      <div className="pointer-events-none absolute -left-12 -top-14 size-36 rounded-full bg-emerald-400/20 blur-2xl" />

      {/* Icône pastille */}
      <div className="relative grid size-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-md shadow-emerald-500/30">
        <BellRingingIcon size={22} weight="duotone" />
        <span className="absolute right-1 top-1 size-1.5 rounded-full bg-white" />
      </div>

      {/* Contenu */}
      <div className="relative min-w-0 flex-1">
        <p className="text-sm font-bold leading-snug text-slate-900">
          Ne ratez plus vos alertes importantes
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-slate-500">
          Paiements, récompenses de parrainage et sécurité, même quand vous n&rsquo;êtes pas sur
          l&rsquo;application.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-emerald-500 px-3.5 text-[13px] font-semibold text-white shadow-sm shadow-emerald-500/30 transition-all hover:bg-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 active:scale-95 disabled:pointer-events-none disabled:opacity-60"
          >
            {busy ? (
              <>
                <span className="size-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Activation…
              </>
            ) : (
              <>
                <CheckCircleIcon size={16} weight="bold" />
                Activer les notifications
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onLater}
            className="inline-flex h-9 items-center justify-center rounded-lg px-3 text-[13px] font-medium text-slate-500 transition-colors hover:bg-emerald-50 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"
          >
            Plus tard
          </button>
        </div>
      </div>

      {/* Fermeture */}
      <button
        type="button"
        aria-label="Fermer la notification"
        onClick={onClose}
        className="relative grid size-7 shrink-0 place-items-center rounded-md text-slate-400 transition-colors hover:bg-emerald-50 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"
      >
        <XIcon size={14} />
      </button>
    </div>
  );
};

export const PushOptInNudge = () => {
  const { user } = useUser();
  const push = usePushNotifications();
  const location = useLocation();
  const { dismiss } = useToast();

  // Verrou de session : une seule AFFICHAGE par session (le localStorage fait
  // le reste de la cadence sur cet appareil). Posé au moment réel de
  // l'affichage, pas à l'armement : si le timer est annulé (churn de
  // dépendances, navigation), l'effet peut réarmer — cf. #116.
  const settledRef = useRef(false);

  /**
   * Diagnostic (#116) : trace la gate bloquante. Une pause — notamment la
   * pause 14 j — n'a jamais le droit d'être silencieuse.
   */
  const debugGate = (reason: string) => {
    if (import.meta.env.DEV) {
      console.debug(`[push-nudge] non affiché : ${reason}`);
    }
  };

  useEffect(() => {
    const pathname = location.pathname;
    const inClientSpace =
      pathname.startsWith("/client") ||
      pathname.startsWith("/builder") ||
      pathname.startsWith("/artboard");

    if (settledRef.current) return;
    if (!user?.id) return debugGate("utilisateur non connecté");
    if (!inClientSpace) return debugGate("hors de l'espace client");
    // Anti-churn (#116) : n'armer le timer qu'une fois l'état push résolu
    // (config serveur + sync navigateur), sinon les dépendances « churnent »
    // pendant le chargement et le timer est annulé avant tout affichage.
    if (!push.ready) return debugGate("état push non résolu (chargement)");
    if (!push.supported || !push.serverEnabled)
      return debugGate("push non supporté ou désactivé côté serveur");
    if (push.permission === "denied") return debugGate("permission refusée");
    if (push.subscribed) return debugGate("déjà abonné");
    if (isPushNudgeOptedIn()) return debugGate("opt-in déjà consenti");

    const blockReason = getPushNudgeBlockReason();
    if (blockReason) return debugGate(blockReason);

    const timer = window.setTimeout(() => {
      // Verrou de session posé ici, au moment réel de l'affichage.
      settledRef.current = true;

      // L'impression n'est comptée qu'au moment réel de l'affichage : si le
      // délai d'attente est interrompu (changement de page, démontage), aucune
      // impression n'est consommée (règle web.dev, cf. soft-prompt).
      recordPushNudgeShown();

      const toastId = toast.custom(
        (id) => (
          <NudgeCard
            toastId={id}
            onEnable={async () => {
              const result = await push.subscribe();
              if (result.ok) {
                recordPushOptedIn();
                toast.success("Notifications push activées 🎉", {
                  description: "Vous recevrez désormais vos alertes même application fermée.",
                  duration: 5_000,
                });
              } else if (result.reason === "denied") {
                recordPushNudgeDenied();
                toast.error("Notifications bloquées par le navigateur", {
                  description:
                    "Cliquez sur le cadenas 🔒 dans la barre d'adresse, autorisez les notifications pour ce site, puis réessayez depuis Paramètres → Notifications.",
                  duration: 8_000,
                });
              } else {
                // Échec transitoire → on retente plus tard, sans marteler.
                recordPushNudgeClosed();
              }
            }}
            onLater={() => {
              recordPushNudgeLater();
              dismiss(toastId);
            }}
            onClose={() => {
              recordPushNudgeClosed();
              dismiss(toastId);
            }}
          />
        ),
        {
          unstyled: true,
          duration: NUDGE_DURATION_MS,
        },
      );

      // Sécurité : si l'utilisateur ne fait rien, la carte disparaît seule.
      window.setTimeout(() => dismiss(toastId), NUDGE_DURATION_MS);
    }, NUDGE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [
    user?.id,
    location.pathname,
    push.ready,
    push.supported,
    push.serverEnabled,
    push.permission,
    push.subscribed,
  ]);

  return null;
};
